import { AuthService } from '@/shared/services';
import { Component, inject, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { DiagnosticCheckService } from '../diagnostic-check/services/diagnostic-check.service';

@Component({
  selector: 'vm-order-tv-remote-shell',
  template: ` <vm-strapi-page-shell *ngIf="strapiUID && showArticle" [strapiUID]="strapiUID"></vm-strapi-page-shell>`,
})
export class OrderTvRemoteShellComponent implements OnInit {
  strapiUID = 'dc_order-tv-remote-verify';
  activeRoute = 'start';
  tvBoxTypes = [];
  customerInfo = null;
  showArticle = false;

  private diagnosticCheckService = inject(DiagnosticCheckService);
  private authService = inject(AuthService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  
  ngOnInit() {
    this.showPage();
  }

  showPage() {
    this.route.paramMap.subscribe((params) => {
      const page = params.get('page') || 'start';

      this.showArticle = false;
      switch (page) {
        case 'redirect':
          this.strapiUID = '';
          this.checkStatus();
          break;
        case 'start':
          this.checkAuth();
          break;
        case 'login':
          this.strapiUID = '';
          this.login();
          break;
        case 'unauthenticated':
          this.strapiUID = 'dc_order-tv-remote-verify';
          break;
        case 'no-tv-services':
          this.strapiUID = 'dc_order-tv-remote-no-tv-services';
          break;
        case 'already-ordered':
          this.strapiUID = 'dc_order-tv-remote-already-ordered';
          break;
        case 'tivo':
          this.strapiUID = 'dc_order-tv-remote-confirm-v6-tivo';
          break;
        case 'tivo-success':
          this.strapiUID = 'dc_order-tv-remote-success-v6-tivo';
          break;
        case 'v6':
          this.strapiUID = 'dc_order-tv-remote-confirm-v6-tivo';
          break;
        case 'v6-success':
          this.strapiUID = 'dc_order-tv-remote-success-v6-tivo';
          break;
        case 'stream':
          this.strapiUID = 'dc_order-tv-remote-confirm-360-stream';
          break;
        case 'stream-success':
          this.strapiUID = 'dc_order-tv-remote-success-360-stream';
          break;
        case '360':
          this.strapiUID = 'dc_order-tv-remote-confirm-360-stream';
          break;
        case '360-success':
          this.strapiUID = 'dc_order-tv-remote-success-360-stream';
          break;
        case 'free':
          this.strapiUID = 'dc_order-tv-remote-free';
          break;
        default:
          console.error(`Unhandled page: ${page}`);
      }
      this.showArticle = true;
    });
  }

  private login() {
    const currentUrl = this.router.url;
    const baseUrl = currentUrl.substring(0, currentUrl.lastIndexOf('/'));
    const returnUrl = `${baseUrl}/redirect`;
    sessionStorage.setItem('vmo2_zeus_returnUrl', returnUrl);
    this.router.navigate(['/~api/pingfed-login']);
  }

  private checkAuth() {
    if (this.authService.isAuthenticated()) {
      this.checkStatus();
    } else {
      this.router.navigate(['../unauthenticated'], { relativeTo: this.route });
    }
  }

  private checkStatus() {
    this.diagnosticCheckService.getAccountCheck().subscribe(
      (response) => {
        this.tvBoxTypes = this.diagnosticCheckService.getTVBoxTypes(true);
        const supportedTvBoxes = ['360', 'v6', 'tivo', 'stream'];
        const hasSupportedTvBoxes = supportedTvBoxes?.some((tv) => this.tvBoxTypes?.includes(tv));
        const tvCustomer = response?.equipmentAndProductDetails?.productSet?.includes('TV');

        if (!hasSupportedTvBoxes && tvCustomer) {
          this.router.navigate(['../older-tv-box'], { relativeTo: this.route });
        } else if (!hasSupportedTvBoxes || !tvCustomer) {
          this.router.navigate(['../no-tv-services'], { relativeTo: this.route });
        } else {
          this.checkCustomerInfo();
        }
      },
      (error) => {
        console.error('Error during account check:', error);
        this.router.navigate(['../api-failure'], { relativeTo: this.route });
      }
    );
  }

  private checkCustomerInfo() {
    this.diagnosticCheckService.getCustomerInfo().subscribe(
      (result) => {
        this.customerInfo = result;
        if (result?.lastRemoteOrderDate && this.isDateInLast5Days(result?.lastRemoteOrderDate)) {
          this.router.navigate(['../already-ordered'], { relativeTo: this.route });
        } else if (result?.isNextRemoteOrderFOC) {
          this.router.navigate(['../free'], { relativeTo: this.route });
        } else {
          if (result?.overMaxRemoteOrdersThreshold) {
            this.router.navigate(['../place-order-offline'], { relativeTo: this.route });
          } else {
            if (this.tvBoxTypes.includes('tivo')) {
              this.router.navigate(['../tivo'], { relativeTo: this.route });
            } else if (this.tvBoxTypes.includes('v6')) {
              this.router.navigate(['../v6'], { relativeTo: this.route });
            } else if (this.tvBoxTypes.includes('360')) {
              this.router.navigate(['../360'], { relativeTo: this.route });
            } else {
              this.router.navigate(['../stream'], { relativeTo: this.route });
            }
          }
        }
      },
      (error) => {
        console.error('Error fetching customer info:', error);
        this.router.navigate(['../api-failure'], { relativeTo: this.route });
      }
    );
  }

  isDateInLast5Days(dateString?: string): boolean {
    if (dateString) {
      const currentDate = new Date();
      const inputDate = new Date(dateString);
      const differenceInMilliseconds = currentDate.getTime() - inputDate.getTime();
      const differenceInDays = differenceInMilliseconds / (1000 * 60 * 60 * 24);
      return differenceInDays <= 5 && differenceInDays >= 0;
    }
    return false;
  }
}
